'use client';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { useCallback } from 'react';

import type { TDynamicData } from '@eliadmin/core/src/types/dynamic-data';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { FaFacebookF, FaLinkedin } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { IoLinkOutline } from 'react-icons/io5';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';

export const ProjectShare = ({ project }: { project: TDynamicData }) => {
  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(window.location.href).then(
      () => {
        toast({
          title: 'Odkaz bol úspešne skopírovaný',
          variant: 'success',
        });
      },
      () => {
        toast({
          title: 'Odkaz sa nepodarilo skopírovať',
          variant: 'destructive',
        });
      },
    );
  }, []);

  const openShareWindow = useCallback(
    (platform: 'facebook' | 'x' | 'linkedin') => {
      const currentUrl = encodeURIComponent(window.location.href);
      const title = encodeURIComponent(project.name || document.title);
      const shareUrls = {
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${currentUrl}`,
        x: `https://twitter.com/intent/tweet?url=${currentUrl}&text=${title}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${currentUrl}`,
      };

      window.open(shareUrls[platform], '_blank', 'noopener,noreferrer');
    },
    [project.name],
  );

  const socials = [
    {
      label: 'Kopírovať odkaz na projekt',
      icon: <IoLinkOutline size={18} />,
      onClick: copyToClipboard,
    },
    {
      label: 'Zdieľať projekt na Facebooku',
      icon: <FaFacebookF size={18} />,
      onClick: () => openShareWindow('facebook'),
    },
    {
      label: 'Zdieľať projekt na X',
      icon: <FaXTwitter size={18} />,
      onClick: () => openShareWindow('x'),
    },
    {
      label: 'Zdieľať projekt na LinkedIn',
      icon: <FaLinkedin size={18} />,
      onClick: () => openShareWindow('linkedin'),
    },
  ];

  return (
    <div
      className={cn(
        'custom-container',
        'relative mx-auto mb-16 mt-16 flex flex-col items-center justify-center gap-4 py-12 md:py-8',
      )}
    >
      <p className="text-center text-xl font-semibold text-foreground sm:text-2xl">
        ZDIEĽAŤ PROJEKT
      </p>
      <div className="flex items-center justify-center gap-2">
        {socials.map(social => (
          <button
            key={social.label}
            type="button"
            aria-label={social.label}
            onClick={social.onClick}
            className="relative z-[10] flex h-10 w-10 items-center justify-center rounded-full border border-primary bg-transparent bg-white text-primary"
          >
            {social.icon}
          </button>
        ))}
      </div>
      <Image
        src={'/images/design/share-project.svg'}
        alt="Zdieľať projekt"
        fill
        style={{ objectFit: 'cover', objectPosition: 'center' }}
        className="absolute inset-0 z-0 h-full w-full"
      />
    </div>
  );
};
